import { Widget } from '../core/Widget.js';
import { ViewWidget } from '../view/ViewWidget.js';
import { createPopper } from '@popperjs/core';

// DropdownWidget.ts
// Dropdown без jQuery на базе Widget. Глобальный document-click закрывает остальные инстансы.
class DropdownWidget extends Widget {
    constructor() {
        super(...arguments);
        // ---- Инстансовые хэндлеры рядом с методами ----
        /** Клик по кнопке открытия/закрытия */
        this._onButtonClick = (e) => this._toggleDropdown(e);
        /** Ввод в поле поиска */
        this._onSearchInput = (e) => this._handleSearchInput(e);
        /** Изменение «родного» select */
        this._onSelectChange = (e) => this.selectOriginal(e.currentTarget);
        /** Сброс поиска при закрытии (чтобы можно было снять обработчик при destroy) */
        this._onDropdownClosed = () => this.clearSearchInput();
    }
    /** Ключевой класс для поиска виджета */
    static _cssKey() {
        // TODO: Возможно не стоит поддерживать?
        return 'dropdown';
    }
    /** Вешает единый document-click один раз на класс */
    static _ensureDocListener() {
        if (this._docListenerInited)
            return;
        this._docListenerInited = true;
        this._docClickHandler = (e) => {
            const target = e.target;
            const host = target instanceof HTMLElement ? target.closest('.dropdown') : null;
            // Идём по всем .dropdown и закрываем те, которые не содержат таргет
            document.querySelectorAll('.dropdown').forEach(el => {
                const inst = DropdownWidget.from(el); // см. Widget.from():contentReference[oaicite:1]{index=1}
                if (!inst)
                    return;
                // Если клик вне любого .dropdown — закрыть все
                // Если клик внутри конкретного .dropdown — закрыть все остальные
                if (!host || el !== host) {
                    inst.closeDropdown();
                }
            });
        };
        document.addEventListener('click', this._docClickHandler);
    }
    // -------------------- Инициализация --------------------
    /** Инициализация (аналог _create у jQuery-виджета) */
    _create(op) {
        const el = this.element;
        DropdownWidget._ensureDocListener();
        // Включить поиск
        if (op.search) {
            el.classList.add('dropdown_searchable');
        }
        // Спец-логика для filemanager
        if (el.closest('.im-filemanager')) {
            this._onHostChange = () => {
                const value = el.dataset.value ?? '';
                const text = el.dataset.text ?? '';
                const view = ViewWidget.closestView(el);
                if (!view)
                    return;
                const linkCtn = view.dom.querySelector('.link-for-dropdown');
                if (linkCtn) {
                    linkCtn.innerHTML = `<a href="/server-this/storage/projects/${value}/" target="_blank">${text}</a>`;
                }
                const textCtn = view.dom.querySelector('.text-for-dropdown');
                if (textCtn)
                    textCtn.textContent = text;
            };
            el.addEventListener('change', this._onHostChange);
        }
        // Кнопка
        const btn = el.querySelector('.dropdown__button');
        if (btn)
            btn.addEventListener('click', this._onButtonClick);
        // Fixed-позиционирование
        this._positionFixed = op.fixed ?? false;
        // Использовать регекс
        this._useRegex = op.regex ?? false;
        // Опции
        el.querySelectorAll('.dropdown__option').forEach(opt => {
            opt.addEventListener('click', (e) => {
                e.preventDefault();
                const value = opt.dataset.value ?? '';
                this.selectValue(value, false, opt.innerHTML);
            });
        });
        // <select>
        const select = el.querySelector('select');
        if (select)
            select.addEventListener('change', this._onSelectChange);
        // Поиск
        const search = el.querySelector('.dropdown__search');
        if (search)
            search.addEventListener('input', this._onSearchInput);
        // Сброс поиска при закрытии
        el.addEventListener('dropdown-closed', this._onDropdownClosed, { passive: true });
    }
    // -------------------- Фильтрация --------------------
    /**
     * Фильтрация опций (учитывает data-regex).
     * @param input Поле ввода (если есть).
     */
    filterList(input) {
        const el = this.element;
        const text = (input?.value ?? '').toLowerCase();
        if (this._useRegex) {
            const regx = text === '' ? '' : new RegExp(String.raw `(?<!\p{L}\p{M}*|[\p{N}_])${text}`, 'iu');
            el.querySelectorAll('.dropdown__option').forEach(opt => {
                const matched = regx === '' ? true : regx.test(opt.textContent?.toLowerCase() ?? '');
                opt.style.display = matched ? '' : 'none';
            });
        }
        else {
            el.querySelectorAll('.dropdown__option').forEach(opt => {
                const t = (opt.textContent ?? '').toLowerCase();
                opt.style.display = t.includes(text) ? '' : 'none';
            });
        }
    }
    /** Сброс фильтра и поля поиска */
    clearSearchInput() {
        this.element.querySelectorAll('.dropdown__option').forEach(opt => (opt.style.display = ''));
        const search = this.element.querySelector('.dropdown__search');
        if (search)
            search.value = '';
    }
    // -------------------- Открытие/закрытие --------------------
    /** Закрыть дропдаун программно */
    closeDropdown() {
        const el = this.element;
        const btn = el.querySelector('.dropdown__button');
        const list = el.querySelector('.dropdown__list');
        const wrap = el.querySelector('.dropdown__wrapper');
        if (btn?.classList.contains('dropdown__button_active')) {
            btn.classList.remove('dropdown__button_active');
            if (wrap)
                wrap.classList.remove('dropdown__wrapper_active');
            if (list)
                list.classList.remove('dropdown__list_active');
            el.dispatchEvent(new CustomEvent('dropdown-closed', { bubbles: true }));
        }
    }
    // Клик по кнопке/контейнеру (кроме .dropdown__list)
    _toggleDropdown(e) {
        e.preventDefault();
        const el = this.element;
        const target = e.target;
        if (target && target.closest('.dropdown__list'))
            return;
        const btn = el.querySelector('.dropdown__button');
        const list = el.querySelector('.dropdown__list');
        const wrap = el.querySelector('.dropdown__wrapper');
        if (!btn || !list)
            return;
        btn.classList.toggle('dropdown__button_active');
        if (wrap)
            wrap.classList.toggle('dropdown__wrapper_active');
        list.classList.toggle('dropdown__list_active');
        if (this._positionFixed) {
            createPopper(btn, list, {
                placement: 'bottom-start',
                modifiers: [
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: ['top-end', 'bottom-start'],
                        }
                    }
                ]
            });
            list.style.position = 'fixed';
            list.style.width = `${btn.getBoundingClientRect().width}px`;
        }
        else {
            list.style.position = '';
            list.style.left = '';
            list.style.top = '';
            list.style.width = '';
        }
        if (btn.classList.contains('dropdown__button_active')) {
            el.dispatchEvent(new CustomEvent('dropdown-opened', { bubbles: true }));
            const search = el.querySelector('.dropdown__search');
            if (search)
                window.setTimeout(() => search.focus(), 300);
        }
        else {
            el.dispatchEvent(new CustomEvent('dropdown-closed', { bubbles: true }));
        }
    }
    // -------------------- Значение --------------------
    /**
     * Вернуть основное значение виджета.
     */
    getValue() {
        const raw = this.element.dataset.value;
        // TODO: Стоит ли поддерживать dbnull?
        const value = raw === "" || raw === undefined
            ? null
            : raw;
        return value;
    }
    /**
     * Установить основное значение
     * @param val - устанавливаемое значение
     */
    setValue(val) {
        this.selectValue(val, true, undefined);
        return true;
    }
    /** true — если виджет считает, что значение задано */
    hasValue() {
        return this.element.dataset.value !== undefined
            && this.element.dataset.value !== '';
    }
    /**
     * Установить значение (как клик по опции).
     * @param value Значение (value).
     * @param noChange Если true — не триггерить событие ddchange.
     * @param optionHtml HTML для кнопки (если не задан — берём текст опции).
     */
    selectValue(value, noChange, optionHtml) {
        const val = value ?? "";
        const el = this.element;
        const select = el.querySelector('select');
        if (!select)
            return;
        const option = this._getOptionByValue(select, val);
        if (!option)
            return;
        // храним в dataset то, что выбрали
        // 2026-02-25 перенес эту строку ниже,
        // была раньше до !option, но тогда могут возникать когда установлены значения другие, а визуально выбрано старое значение
        el.dataset.value = val;
        // ✅ обновляем через property
        for (const o of Array.from(select.options)) {
            o.removeAttribute('selected');
        }
        option.setAttribute('selected', 'selected');
        this._selectOption(option, !!noChange, optionHtml);
    }
    // ▼ добавь приватный хелпер
    _getOptionByValue(select, value) {
        for (const o of Array.from(select.options)) {
            if (o.value === value)
                return o;
        }
        return null;
    }
    /**
     * Реакция на выбор «родной» опции select.
     * @param selectElement Сам select.
     */
    selectOriginal(selectElement) {
        const value = selectElement.value;
        const text = selectElement.selectedOptions[0]?.text ?? '';
        this.element.dataset.value = value;
        this.element.dataset.text = text;
        const btn = this.element.querySelector('.dropdown__button');
        if (btn)
            btn.textContent = text;
        this.closeDropdown();
        this.element.dispatchEvent(new CustomEvent('ddchange', { bubbles: true }));
    }
    /** Обработка input поиска */
    _handleSearchInput(e) {
        const input = e.currentTarget;
        this.filterList(input);
    }
    /** Применить выбор по самому <option> */
    _selectOption(optionItem, noChange, optionHtml) {
        const value = optionItem.dataset.value ?? optionItem.value;
        const text = optionItem.text;
        this.element.dataset.value = value;
        this.element.dataset.text = text;
        const btn = this.element.querySelector('.dropdown__button');
        if (btn)
            btn.innerHTML = optionHtml === undefined ? this._escapeHtml(text) : optionHtml;
        this.closeDropdown();
        if (!noChange) {
            this.element.dispatchEvent(new CustomEvent('ddchange', { bubbles: true }));
        }
    }
    // -------------------- Приватные утилиты --------------------
    _escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    // -------------------- Жизненный цикл --------------------
    /**
     * Корректное уничтожение виджета.
     * Важно: вызывается родителем (Widget) — дополнительный публичный destroy() не нужен.
     */
    _onDestroy() {
        // Глобальный document-click не снимаем: он общий и лёгкий, работает через WeakMap (Widget.from):contentReference[oaicite:2]{index=2}
    }
}
/** Имя виджета */
DropdownWidget._alias = "Dropdown";

export { DropdownWidget };
